import { useIntl } from 'react-intl';
import { Button, Checkbox, Divider, Input, TextArea } from '../../../../ui';
import { useEffect, useRef, useState } from 'react';
import useQueryApiClient from '../../../../utils/useQueryApiClient';
import { FarmType } from '../../../../interfaces/BackendModels';
import { StyledBusinessTab } from '../style';
import useFormInstance from 'antd/es/form/hooks/useFormInstance';
import { WithoutModalAddressFinder } from 'ui/AdressSearch/WithoutModal';
import { Alert, Form } from 'antd';
import { SocialNetwork } from 'types/SocialNetwork';
import FarmLocationIssue from 'enums/farm-location-issue';

interface Options {
  label: string;
  value: number;
}

interface BaseInformationProps {
  isAdmin?: boolean;
  onAddPvdNumberClick?: () => void;
  publicAllowed?: boolean;
}
export const BaseInformation = ({
  isAdmin = false,
  onAddPvdNumberClick,
  publicAllowed = false,
}: BaseInformationProps) => {
  const [options, setOptions] = useState<Options[]>([]);
  const intl = useIntl();
  const form = useFormInstance();
  const isPublicProfile = Form.useWatch('public', form);
  const pvdNr: string | undefined = Form.useWatch('pvd_nr', form);
  const registeredFullAddress = Form.useWatch('registered_full_address', form);
  const registeredAddress = Form.useWatch('registered_address', form);
  const registeredIsActual = Form.useWatch(
    'registered_address_is_actual_address',
    form
  );
  const companyType = Form.useWatch('company_type', { form, preserve: true });
  const locationIssue: FarmLocationIssue | null | undefined = Form.useWatch(
    'location_issue',
    { form, preserve: true }
  );
  const isNaturalPerson = companyType === null;
  const canEditAddress = isAdmin || isNaturalPerson;
  const requiresAddress = !isAdmin && isNaturalPerson;

  const wasRegisteredActual = useRef(registeredIsActual);

  useEffect(() => {
    if (isNaturalPerson && registeredIsActual) {
      form.setFieldValue('actual_address', registeredAddress ?? null);
    }
  }, [isNaturalPerson, registeredIsActual, registeredAddress, form]);

  useEffect(() => {
    if (wasRegisteredActual.current && registeredIsActual === false) {
      form.setFields([
        { name: 'actual_address', value: null },
        { name: 'actual_lks_lat', value: null },
        { name: 'actual_lks_long', value: null },
      ]);
    }
    wasRegisteredActual.current = registeredIsActual;
  }, [registeredIsActual, form]);

  useQueryApiClient({
    request: {
      url: '/api/v2/farm-types',
    },
    onSuccess: (response) => handleOptionsParse(response),
  });

  const { data: socialNetworks } = useQueryApiClient({
    request: {
      url: '/api/v2/social-networks',
    },
  });

  const handleOptionsParse = (options: FarmType[]) => {
    const parsedOptions: Options[] = options
      .map((option: FarmType) => ({
        label: option.name ?? '',
        value: option.id ?? 0,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));

    setOptions(parsedOptions);
  };

  return (
    <StyledBusinessTab>
      <div className={'content'}>
        <div className={'section'}>
          <div className={'title'}>
            <h3>{intl.formatMessage({ id: 'navigation.base_information' })}</h3>
          </div>
          <Input
            label={intl.formatMessage({ id: 'company.name' })}
            layout={'vertical'}
            name={'name'}
            className={'label-fix'}
            disabled
          />

          <div className={'double-input'}>
            <Input
              label={intl.formatMessage({ id: 'company.reg_nr' })}
              layout={'vertical'}
              name={'reg_nr'}
              className={'label-fix'}
              disabled
            />

            {canEditAddress ? (
              <WithoutModalAddressFinder
                form={form}
                name={'registered_address'}
                latName={'registered_lks_lat'}
                longName={'registered_lks_long'}
                requireCoordinates
                label={intl.formatMessage({ id: 'company.registered_address' })}
                layout="vertical"
                disabled={false}
                validations={requiresAddress ? ['required'] : []}
              />
            ) : (
              <Input
                label={intl.formatMessage({ id: 'company.registered_address' })}
                layout={'vertical'}
                name={
                  registeredFullAddress
                    ? 'registered_full_address'
                    : 'registered_address'
                }
                className={'label-fix'}
                disabled
              />
            )}
          </div>

          <div className={'double-checkbox'}>
            <Checkbox
              name={'public'}
              label={intl.formatMessage({ id: 'company.public' })}
              disabled={!publicAllowed}
            />

            {isNaturalPerson && (
              <Checkbox
                name={'registered_address_is_actual_address'}
                label={intl.formatMessage({
                  id: 'company.reg_address_is_actual_address',
                })}
              />
            )}
          </div>

          {isNaturalPerson && (
            <div className={'double-input'}>
              <WithoutModalAddressFinder
                form={form}
                name={'actual_address'}
                latName={'actual_lks_lat'}
                longName={'actual_lks_long'}
                requireCoordinates
                label={intl.formatMessage({ id: 'company.actual_address' })}
                layout="vertical"
                disabled={!!registeredIsActual}
                validations={requiresAddress ? ['required'] : []}
              />
            </div>
          )}

          {!!locationIssue && (
            <Alert
              className={'location-issue'}
              type={'warning'}
              showIcon
              message={intl.formatMessage({
                id: `company.location_issue.${locationIssue}`,
              })}
            />
          )}

          <div className={'double-input'}>
            <Input
              label={intl.formatMessage({ id: 'company.pvn_number' })}
              layout={'vertical'}
              name={'pvn_nr'}
              className={'label-fix'}
              disabled
            />

            <Input
              label={intl.formatMessage({ id: 'company.pvd_number' })}
              layout={'vertical'}
              name={'pvd_nr'}
              disabled
              className={'label-fix'}
            />
          </div>
          {!pvdNr && (
            <div className={'add-pvd-number-row'}>
              <Button
                label={intl.formatMessage({ id: 'company.add_pvd_number' })}
                type={'primary'}
                className={'new-btn'}
                onClick={onAddPvdNumberClick}
              />
            </div>
          )}
        </div>
        <Divider />
        <div className={'section'}>
          <div className={'title'}>
            <h3>
              {intl.formatMessage({ id: 'general.additional_information' })}
            </h3>
          </div>
          <Input
            label={intl.formatMessage({ id: 'general.brand_name' })}
            name={'brand'}
            validations={!isAdmin && isPublicProfile ? ['required'] : []}
            dependencies={['public']}
            className={'label-fix'}
          />
          <TextArea
            label={intl.formatMessage({
              id: 'general.short_company_description',
            })}
            className={'text-area'}
            maxLength={250}
            showCount
            name={'short_description'}
            validations={!isAdmin && isPublicProfile ? ['required'] : []}
          />
          <TextArea
            label={intl.formatMessage({
              id: 'general.company_description',
            })}
            maxLength={3000}
            className={'text-area'}
            name={'description'}
            validations={!isAdmin && isPublicProfile ? ['required'] : []}
          />

          <div className={'double-input'}>
            <Input
              label={intl.formatMessage({ id: 'general.phone' })}
              className={'label-fix'}
              validations={
                !isAdmin ? ['phoneNumber', 'required'] : ['phoneNumber']
              }
              name={'contact_phone'}
            />
            <Input
              label={intl.formatMessage({ id: 'general.email' })}
              className={'label-fix'}
              validations={!isAdmin ? ['email', 'required'] : ['email']}
              name={'contact_email'}
            />
          </div>
          <div className={'double-input'}>
            <Input
              label={intl.formatMessage({ id: 'general.website' })}
              className={'label-fix'}
              name={'website'}
            />
          </div>
          {!!socialNetworks?.length && (
            <div className={'double-input wrap'}>
              {socialNetworks.map((network: SocialNetwork) => (
                <Input
                  key={network.id}
                  label={network.name}
                  className={'label-fix'}
                  name={['social_links', String(network.id)]}
                  placeholder={intl.formatMessage({
                    id: 'social_networks.link_placeholder',
                  })}
                />
              ))}
            </div>
          )}
        </div>
        <Divider />
        <div className={'section'}>
          <div className={'title'}>
            <h3>{intl.formatMessage({ id: 'general.company_type' })}</h3>
          </div>
          <div className={'double-column-checkbox'}>
            <Checkbox options={options} name={'types'} />
          </div>
        </div>
      </div>
    </StyledBusinessTab>
  );
};
